import { api } from '../lib/axios';

export interface PackagingConfig {
  id: string;
  productBatchId: string;
  unitsPerCase: number;
  casesPerPallet: number;
  caseDimensions?: string;
  palletDimensions?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Package {
  id: string;
  packageType: 'CASE' | 'PALLET';
  qrToken: string;
  status: string;
  createdAt: string;
  brokenDownAt?: string;
  currentOwnerId?: string;
  batch?: {
    id: string;
    productName: string;
    manufacturer: any;
  };
  currentOwner?: {
    id: string;
    fullName: string;
    email: string;
    roleLevel: number;
  };
  parentPackage?: {
    id: string;
    packageType: string;
    qrToken: string;
  };
}

export interface PackageWithActivation extends Package {
  productName: string;
  totalProducts: number;
  activatedProducts: number;
  activationStatus: 'not_activated' | 'partially_activated' | 'fully_activated';
  canActivate: boolean;
}

export interface PackageActivationResult {
  success: boolean;
  packageId: string;
  packageType: string;
  productName: string;
  manufacturer: string;
  results: {
    totalProducts: number;
    alreadyActivated: number;
    newlyActivated: number;
    failed: number;
    activatedProducts: Array<{
      id: string;
      serialNumber: number;
    }>;
    failedProducts: Array<{
      productId: string;
      error: string;
    }>;
  };
  error?: string;
}

export interface PackageContents {
  package: Package;
  childPackages: Package[];
  products: any[];
  totalUnits: number;
}

class PackagingService {
  /**
   * Create packaging configuration for a batch
   */
  async createConfig(
    batchId: string,
    config: {
      unitsPerCase: number;
      casesPerPallet: number;
      caseDimensions?: string;
      palletDimensions?: string;
    }
  ) {
    const response = await api.post(`/packaging-config/batch/${batchId}`, config);
    return response.data;
  }

  /**
   * Get packaging configuration for a batch
   */
  async getConfig(batchId: string) {
    const response = await api.get(`/packaging-config/batch/${batchId}`);
    return response.data;
  }

  /**
   * Update packaging configuration
   */
  async updateConfig(
    batchId: string,
    updates: {
      unitsPerCase?: number;
      casesPerPallet?: number;
      caseDimensions?: string;
      palletDimensions?: string;
    }
  ) {
    const response = await api.put(`/packaging-config/batch/${batchId}`, updates);
    return response.data;
  }

  /**
   * Delete packaging configuration
   */
  async deleteConfig(batchId: string) {
    const response = await api.delete(`/packaging-config/batch/${batchId}`);
    return response.data;
  }

  /**
   * Get all packaging configurations for a manufacturer
   */
  async getManufacturerConfigs(manufacturerId: string) {
    const response = await api.get(`/packaging-config/manufacturer/${manufacturerId}`);
    return response.data;
  }

  /**
   * Get package by QR token
   */
  async getPackageByQR(qrToken: string) {
    const response = await api.get(`/packages/${qrToken}`);
    return response.data;
  }

  /**
   * Get package contents
   */
  async getPackageContents(qrToken: string) {
    const response = await api.get(`/packages/${qrToken}/contents`);
    return response.data;
  }

  /**
   * Transfer package to new owner
   */
  async transferPackage(qrToken: string, recipientId: string) {
    const response = await api.post(`/packages/${qrToken}/transfer`, { recipientId });
    return response.data;
  }

  /**
   * Get packages for a batch with activation status
   */
  async getBatchPackagesWithActivation(batchId: string): Promise<{ success: boolean; data?: PackageWithActivation[]; error?: string }> {
    try {
      const response = await api.get(`/packages/batch/${batchId}/packages`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get batch packages'
      };
    }
  }

  /**
   * Activate all products in a package (bulk activation)
   */
  async activatePackage(packageId: string): Promise<PackageActivationResult> {
    try {
      const response = await api.post(`/packages/${packageId}/activate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to activate package'
      } as PackageActivationResult;
    }
  }

  /**
   * Bulk activate multiple packages
   */
  async bulkActivatePackages(packageIds: string[]): Promise<{ 
    success: boolean; 
    results?: {
      successful: number;
      failed: number;
      totalProductsActivated: number;
      packages: PackageActivationResult[];
      errors: Array<{ packageId: string; error: string }>;
    }; 
    error?: string 
  }> {
    try {
      const response = await api.post('/packages/activate/bulk', { packageIds });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to bulk activate packages'
      };
    }
  }

  /**
   * Mark package as broken down
   */
  async breakdownPackage(qrToken: string) {
    const response = await api.post(`/packages/${qrToken}/breakdown`, {});
    return response.data;
  }

  /**
   * Get all packages for a batch
   */
  async getBatchPackages(batchId: string) {
    const response = await api.get(`/packages/batch/${batchId}/packages`);
    return response.data;
  }

  /**
   * Create packages (cases and pallets) for a batch
   */
  async createPackages(batchId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await api.post(`/packaging-config/batch/${batchId}/create-packages`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create packages'
      };
    }
  }

  /**
   * Request OTP for package activation (dealer flow)
   */
  async requestPackageActivationOTP(qrToken: string, dealerId: string): Promise<{ 
    success: boolean; 
    otpId?: string;
    expiryTime?: string;
    deliveryStatus?: string;
    attemptsRemaining?: number;
    error?: string 
  }> {
    try {
      const response = await api.post(`/packages/${qrToken}/request-activation`, { dealerId });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to request activation OTP'
      };
    }
  }

  /**
   * Verify OTP and activate package (dealer flow)
   */
  async verifyPackageActivationOTP(qrToken: string, dealerId: string, otpCode: string): Promise<{ 
    success: boolean; 
    valid?: boolean;
    packageActivated?: boolean;
    message?: string;
    attemptsRemaining?: number;
    error?: string 
  }> {
    try {
      const response = await api.post(`/packages/${qrToken}/verify-activation`, { 
        dealerId, 
        otpCode 
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify activation OTP'
      };
    }
  }
}

export const packagingService = new PackagingService();
